import { useState, useEffect } from "react";
import { analyticsApi } from "@/services/apiClient";
import { todayISODate } from "@/stores/filterStore";
import { fmt } from "@/lib/format";
import { topEngineByRequests } from "@/lib/briefing";
import { buildBriefingEvidence } from "@/lib/briefingEvidence";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ChevronRight, Lightbulb, Wrench, Sparkles } from "lucide-react";

// 오늘의 브리핑 V2-P0 — AI 운영 분석가(deterministic). LLM 없음(V2-P1 예정).
// API 7회(allSettled, 병렬): overview(today), timeseries(D-7~today, hour), by-tool(today),
// by-model(today), cx/tickets, devops(health), mystyle(today). 한 곳 실패해도 계속 동작.
// evidence 조립·relationship·PM/Dev action 은 lib/briefingEvidence.js(순수). same-time 정본.

function todayKST() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  const kst = new Date(d.getTime() + (540 + off) * 60000);
  return kst.toISOString().slice(0, 10);
}
function nowKstHour() {
  const d = new Date();
  const kst = new Date(d.getTime() + (540 + d.getTimezoneOffset()) * 60000);
  return kst.getHours();
}
function daysAgoISO(iso, n) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function BriefingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ai, setAi] = useState({ status: "loading", narrative: null }); // loading|ready|unavailable

  useEffect(() => {
    const ctrl = new AbortController();
    const today = todayISODate();
    const sevenAgo = daysAgoISO(today, 7);
    const kstToday = todayKST();
    const kstHour = nowKstHour();
    const S = ctrl.signal;

    async function load() {
      try {
        const [ovRes, tsRes, toolRes, modelRes, cxRes, devRes, msRes] = await Promise.allSettled([
          analyticsApi.overview({ from: today, to: today, scope: "customer" }, S),
          analyticsApi.timeseries({ from: sevenAgo, to: today, granularity: "hour", scope: "customer" }, S),
          analyticsApi.byTool({ from: today, to: today, scope: "customer" }, S),
          analyticsApi.byModel({ from: today, to: today, scope: "customer" }, S),
          analyticsApi.cx.tickets({ state: "all" }, S),
          analyticsApi.devops({ from: today, to: today, part: "health" }, S).catch(() => null),
          analyticsApi.mystyle({ from: today, to: today }, S).catch(() => null),
        ]);
        const val = (r) => (r.status === "fulfilled" && r.value ? r.value : {});
        const overview = val(ovRes), ts = val(tsRes), tools = val(toolRes),
              modelsR = val(modelRes), cx = val(cxRes), dev = val(devRes), ms = val(msRes);

        const topEngine = topEngineByRequests(tools.tools || []);
        const ticketCount = typeof cx.todayCustomerCount === "number" ? cx.todayCustomerCount : null;

        // devops health → 대표 지표 요약(가능한 필드만; 실패/부재 시 null).
        const health = dev && dev.health ? summarizeHealth(dev.health) : null;

        const evidence = buildBriefingEvidence({
          overviewKpi: overview.kpi || {},
          hourlySeries: Array.isArray(ts.series) ? ts.series : null,
          nowKstHour: kstHour, todayDate: kstToday,
          tools: tools.tools || [],
          models: modelsR.models || [],
          openTicketsToday: ticketCount,
          newTicketsToday: typeof cx.newTodayCount === "number" ? cx.newTodayCount : null,
          vocCount: Array.isArray(cx.voc) ? cx.voc.length : null,
          signupTrend: overview.signupTrend || null,
          newUsersToday: latestNewUsers(overview.signupTrend, kstToday),
          devopsHealth: health,
          mystyle: ms && (ms.adoptionRatePct != null || ms.intentMismatch != null) ? ms : null,
        });

        setData({ kpi: overview.kpi || {}, topEngine, ticketCount, evidence });
      } catch (e) {
        if (e.name !== "AbortError") console.error("[briefing]", e);
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => ctrl.abort();
  }, []);

  // AI narrative — 별도 비차단 조회(§10). 실패/미가용이면 deterministic fallback 사용.
  useEffect(() => {
    const ctrl = new AbortController();
    const today = todayISODate();
    (async () => {
      try {
        const r = await analyticsApi.briefingAi({ from: today, to: today, scope: "customer" }, ctrl.signal);
        if (r && r.available && r.narrative) setAi({ status: "ready", narrative: r.narrative });
        else setAi({ status: "unavailable", narrative: null });
      } catch (e) {
        if (e.name !== "AbortError") setAi({ status: "unavailable", narrative: null });
      }
    })();
    return () => ctrl.abort();
  }, []);

  if (loading) return <BriefingSkeleton />;
  if (!data) return <div className="p-6 text-sm text-ink-400">데이터를 불러오지 못했습니다.</div>;

  return <BriefingContent data={data} ai={ai} />;
}

// ── 공통 presentation — evidence 기반 4-block (desktop/mobile 동일 hierarchy) ──
const SEV_CLS = {
  critical: "border-red-200 bg-red-50 text-red-700",
  warning:  "border-amber-200 bg-amber-50 text-amber-800",
  info:     "border-ink-200 bg-ink-50/60 text-ink-600",
};

// 오늘 한눈에 — AI narrative 우선(ready), 미가용/로딩이면 deterministic headline fallback.
function OverviewBlock({ evidence, ai }) {
  const low = evidence.meta.sampleSize === "low";
  const aiReady = ai && ai.status === "ready" && ai.narrative && ai.narrative.overview;
  return (
    <div className="rounded-xl border border-ink-200 bg-white px-4 py-3">
      {aiReady ? (
        <>
          <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-brand-600">
            <Sparkles className="h-3 w-3" />AI 분석
          </div>
          <div className="text-[15px] font-semibold text-ink-900">{ai.narrative.overview}</div>
          {(ai.narrative.limitations || []).slice(0, 2).map((l, i) => (
            <div key={i} className="mt-1 text-[12px] text-amber-700">{l}</div>
          ))}
        </>
      ) : (
        <>
          <div className="text-[15px] font-semibold text-ink-900">{evidence.headline}</div>
          {low && <div className="mt-1 text-[12px] text-amber-700">표본이 적어 추세 판단에는 제한이 있습니다.</div>}
          {ai && ai.status === "loading" && <div className="mt-1 text-[11px] text-ink-300">AI 분석 준비 중…</div>}
        </>
      )}
    </div>
  );
}

// 주요 인사이트 — AI insights(ready) 우선, 아니면 deterministic signals+relationships.
function InsightsBlock({ evidence, ai }) {
  const aiReady = ai && ai.status === "ready" && ai.narrative && (ai.narrative.insights || []).length > 0;
  if (aiReady) {
    return (
      <div className="space-y-1.5">
        {ai.narrative.insights.slice(0, 3).map((it, i) => (
          <div key={i} className="rounded-xl border border-ink-200 bg-white px-3.5 py-2.5">
            <div className="text-[13px] font-semibold text-ink-800">{it.title}</div>
            <div className="mt-0.5 text-[12px] text-ink-500">{it.description}</div>
          </div>
        ))}
      </div>
    );
  }
  const items = [];
  for (const s of evidence.signals) items.push({ key: `s-${s.type}`, severity: s.severity, text: s.detail, evidence: null });
  for (const r of evidence.relationships) items.push({ key: `r-${r.type}`, severity: r.severity, text: r.suggestedCheck, evidence: r.evidence });
  const top = items.slice(0, 3);
  if (!top.length) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
        <span className="text-[13px] font-medium text-emerald-800">현재 주요 이상 징후·변화가 없습니다.</span>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      {top.map((it) => (
        <div key={it.key} className={`rounded-xl border px-3.5 py-2.5 ${SEV_CLS[it.severity] || SEV_CLS.info}`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 text-[13px] font-medium">{it.text}</span>
          </div>
          {it.evidence && it.evidence.length > 0 && (
            <div className="mt-1 pl-6 text-[11px] text-ink-400">{it.evidence.join(" · ")}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// 추천 확인사항 — AI actions(ready) 우선, 아니면 deterministic. 기획/개발 분리, deep link.
function RecommendBlock({ evidence, ai, navigate }) {
  const aiReady = ai && ai.status === "ready" && ai.narrative;
  const pm = aiReady && (ai.narrative.pmActions || []).length ? ai.narrative.pmActions : (evidence.pmActions || []);
  const dev = aiReady && (ai.narrative.devActions || []).length ? ai.narrative.devActions : (evidence.devActions || []);
  const Group = ({ icon: Icon, title, actions }) => (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-ink-600">
        <Icon className="h-3.5 w-3.5" />{title}
      </div>
      {actions.length ? (
        <div className="space-y-1.5">
          {actions.slice(0, 3).map((a, i) => (
            <button key={a.deepLink || i} type="button" onClick={() => a.deepLink && navigate(a.deepLink)}
              className="flex w-full items-center gap-2 rounded-xl border border-ink-200 bg-white px-3.5 py-2.5 text-left">
              <span className="min-w-0 flex-1 text-[13px] text-ink-800">{a.text}</span>
              {a.deepLink && <ChevronRight className="h-4 w-4 shrink-0 text-ink-400" />}
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-ink-100 bg-ink-50/50 px-3.5 py-2 text-[12px] text-ink-400">확인이 필요한 항목이 없습니다.</div>
      )}
    </div>
  );
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <Group icon={Lightbulb} title="기획자 확인사항" actions={pm} />
      <Group icon={Wrench} title="개발자 확인사항" actions={dev} />
    </div>
  );
}

// 핵심 지표 — 오늘 누적 4지표(부분일 vs 완결일 오해 방지 → '오늘 누적'만).
function KeyMetrics({ evidence }) {
  const b = evidence.business, u = evidence.usage;
  const items = [
    { label: "호출", v: u.requests, f: fmt.int },
    { label: "매출", v: b.revenueKrw, f: fmt.krw },
    { label: "AI 원가", v: b.aiCostKrw, f: fmt.krw },
    { label: "직접비 기여이익", v: b.marginKrw, f: fmt.krw },
  ];
  return (
    <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
      {items.map(({ label, v, f }) => (
        <div key={label} className="min-w-0 rounded-xl bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.05)]">
          <div className="text-[11px] font-medium text-ink-500">{label}</div>
          <div className="mt-1 truncate text-lg font-semibold tabular-nums text-ink-900" title={v != null ? f(v) : "—"}>
            {v != null ? f(v) : "—"}
          </div>
          <div className="mt-0.5 text-[11px] text-ink-400">오늘 누적</div>
        </div>
      ))}
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section>
      <h2 className="mb-1.5 text-[12px] font-semibold text-ink-500">{title}</h2>
      {children}
    </section>
  );
}

// 데스크톱/모바일 공용 데이터를 받아 뷰포트별 presentation 을 고른다(데이터 로직 공유).
function BriefingContent({ data, ai }) {
  const isMobile = useIsMobile();
  return isMobile ? <MobileBriefing data={data} ai={ai} /> : <DesktopBriefing data={data} ai={ai} />;
}

// ── 데스크톱 — 오늘 한눈에 / 주요 인사이트 / 추천 확인사항 / 핵심 지표 + 기존 6 KPI ──
function DesktopBriefing({ data, ai }) {
  const navigate = useNavigate();
  const { kpi, topEngine, ticketCount, evidence } = data;
  const cards = buildCards(kpi, topEngine, ticketCount);
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-bold text-ink-900">오늘의 브리핑</h1>
        <p className="mt-0.5 text-[13px] text-ink-400">
          {todayKST()} 기준 · NOVA 운영 요약
          <span className="ml-1.5 rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-600">고객 기준</span>
        </p>
      </div>
      <Block title="오늘 한눈에"><OverviewBlock evidence={evidence} ai={ai} /></Block>
      <Block title="주요 인사이트"><InsightsBlock evidence={evidence} ai={ai} /></Block>
      <Block title="추천 확인사항"><RecommendBlock evidence={evidence} ai={ai} navigate={navigate} /></Block>
      <Block title="핵심 지표"><KeyMetrics evidence={evidence} /></Block>
      <Block title="현황 지표">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((c) => <BriefingCard key={c.label} {...c} />)}
        </div>
      </Block>
    </div>
  );
}

// ── 모바일 — 동일 hierarchy(오늘 한눈에 → 인사이트 → 추천 → 핵심 지표) ──
function MobileBriefing({ data, ai }) {
  const navigate = useNavigate();
  const { evidence } = data;
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-base font-bold text-ink-900">오늘의 브리핑</h1>
        <p className="mt-0.5 text-[12px] text-ink-400">{todayKST()} · 고객 기준</p>
      </div>
      <Block title="오늘 한눈에"><OverviewBlock evidence={evidence} ai={ai} /></Block>
      <Block title="주요 인사이트"><InsightsBlock evidence={evidence} ai={ai} /></Block>
      <Block title="추천 확인사항"><RecommendBlock evidence={evidence} ai={ai} navigate={navigate} /></Block>
      <Block title="핵심 지표"><KeyMetrics evidence={evidence} /></Block>
      <button type="button" onClick={() => navigate("/dashboard")}
        className="flex w-full items-center justify-between rounded-xl border border-ink-200 bg-white px-4 py-3 text-[14px] font-medium text-ink-700">
        상세 대시보드 보기 <ChevronRight className="h-4 w-4 text-ink-400" />
      </button>
    </div>
  );
}

// devops health 응답 → 대표 지표 요약(가능한 필드만). 구조가 환경마다 달라 안전 추출.
function summarizeHealth(health) {
  try {
    const lambdas = Array.isArray(health.lambdas) ? health.lambdas : (Array.isArray(health) ? health : []);
    let maxErr = null, maxP95 = null;
    for (const l of lambdas) {
      const e = typeof l.errorRatePct === "number" ? l.errorRatePct : null;
      const p = typeof l.p95 === "number" ? l.p95 : (typeof l.p95Ms === "number" ? l.p95Ms : null);
      if (e != null) maxErr = maxErr == null ? e : Math.max(maxErr, e);
      if (p != null) maxP95 = maxP95 == null ? p : Math.max(maxP95, p);
    }
    return { errorRatePct: maxErr, p95LatencyMs: maxP95, deployStatus: null, pipelineStatus: null };
  } catch { return null; }
}

// signupTrend 에서 오늘(KST) 신규 수.
function latestNewUsers(trend, kstToday) {
  if (!Array.isArray(trend)) return null;
  const row = trend.find((r) => r.date === kstToday);
  return row && typeof row.newUsers === "number" ? row.newUsers : null;
}

// KPI 카드 배열 — 데스크톱 뷰 전용(기존 6종 그대로).
function buildCards(kpi, topEngine, ticketCount) {
  return [
    { label: "오늘 답변 호출", value: fmt.int(kpi.requestCount ?? 0), unit: "회", tone: "brand" },
    { label: "최다 사용 엔진", value: topEngine ? topEngine.engine : "—", sub: topEngine ? `${fmt.int(topEngine.requestCount)}회` : undefined, tone: "slate" },
    { label: "오늘 매출", value: fmt.krw(kpi.revenueKrw ?? 0), tone: "green" },
    { label: "추정 AI 원가", value: fmt.krw(kpi.bedrockCostKrw ?? 0), tone: "amber" },
    { label: "직접비 기여이익", value: fmt.krw(kpi.businessMarginKrw ?? 0), tone: (kpi.businessMarginKrw ?? 0) >= 0 ? "green" : "red" },
    { label: "오늘 오류 제보", value: ticketCount == null ? "—" : String(ticketCount), unit: ticketCount == null ? undefined : "건", tone: ticketCount > 0 ? "red" : "slate" },
  ];
}

const TONE_CLASSES = {
  brand: "border-brand-200 bg-brand-50/50",
  green: "border-emerald-200 bg-emerald-50/50",
  amber: "border-amber-200 bg-amber-50/50",
  red: "border-red-200 bg-red-50/50",
  slate: "border-ink-200 bg-ink-50/50",
};

function BriefingCard({ label, value, unit, sub, tone = "slate" }) {
  return (
    <div className={`rounded-xl border px-4 py-4 ${TONE_CLASSES[tone] || TONE_CLASSES.slate}`}>
      <div className="text-[12px] font-medium text-ink-500">{label}</div>
      <div className="mt-1.5 flex items-baseline gap-1">
        <span className="text-2xl font-bold tabular-nums text-ink-900">{value}</span>
        {unit && <span className="text-sm text-ink-400">{unit}</span>}
      </div>
      {sub && <div className="mt-1 text-[12px] text-ink-400">{sub}</div>}
    </div>
  );
}

function BriefingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-6 w-32 animate-pulse rounded bg-ink-100" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-xl border border-ink-100 bg-ink-50/50" />
        ))}
      </div>
    </div>
  );
}


